// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import clearProducts from "@werner/App/Handlers/MiniQuickOrder/ClearProducts";
import reset from "@werner/App/Handlers/MiniQuickOrder/Reset";
import { CustomAppState, XProductInfo } from "@werner/App/Store/Reducers";
import { IconConfig, productAddToCartButtonStyles } from "@werner/Overrides/Components/ProductAddToCartButton";
import { addToCartStatus } from "@werner/XDL/constants";
import Cart from "@werner/XDL/Icons/CartHeader";
import Success2 from "@werner/XDL/Icons/Success2";
import { linkArrowStyle, mergeWithCss } from "@werner/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const combineProducts = (productInfos: XProductInfo[]) => {
    const result: { [key: string]: ProductInfo } = {};
    productInfos.forEach(({ index, ...productInfo }) => {
        if (!result[productInfo.productId]) {
            result[productInfo.productId] = productInfo as ProductInfo;
        } else {
            result[productInfo.productId].qtyOrdered =
                result[productInfo.productId].qtyOrdered + productInfo.qtyOrdered;
        }
    });

    return Object.values(result);
};

const mapStateToProps = (state: CustomAppState) => {
    const settingsCollection = getSettingsCollection(state);
    const productInfos = combineProducts(state.xMiniQuickOrder.productInfos);
    return {
        productInfos,
        products: productInfos.map(o => getProductState(state, o.productId).value),
        cartPageLink: getPageLinkByPageType(state, "CartPage"),
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        allowMultipleWishLists: settingsCollection.wishListSettings.allowMultipleWishLists,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addCartLines: makeHandlerChainAwaitable(addCartLines),
    clearProducts,
    reset,
    loadCurrentCart,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

export interface QuickOrderActionsStyles {
    wrapper?: InjectableCss;
    addAllToCartButton?: ButtonPresentationProps;
    addToListLink?: LinkPresentationProps;
}

export const actionsStyles: QuickOrderActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            gap: 34px;
            padding: 32px 30px;
        `,
    },
    addToListLink: {
        ...linkArrowStyle,
    },
    addAllToCartButton: {
        ...mergeWithCss(productAddToCartButtonStyles, {
            css: css`
                width: 240px;
            `,
        }),
    },
};

const styles = actionsStyles;

const MiniQuickOrderActions = ({
    productInfos,
    products,
    history,
    cartPageLink,
    showAddToCartConfirmationDialog,
    allowMultipleWishLists,
    setAddToListModalIsOpen,
    addCartLines,
    clearProducts,
    reset,
    loadCurrentCart,
}: Props) => {
    const toasterContext = useContext(ToasterContext);

    const [allQtysIsValid, setAllQtysIsValid] = useState(false);
    const [status, setStatus] = useState(addToCartStatus.default);

    const productsExist = productInfos && productInfos.length > 0;
    const canAddSomeToList = products.some(o => o?.canAddToWishlist);

    const iconConfig: IconConfig = {
        src: status === addToCartStatus.success ? Success2 : Cart,
        position: "right",
        ...{ color: "secondary", size: 26 },
    };

    useEffect(() => {
        setAllQtysIsValid(productInfos.every(o => o.qtyOrdered > 0));
    }, [productInfos]);

    useEffect(() => {
        if (status === addToCartStatus.success) {
            setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 800);
        }
    }, [status]);

    const addAllToCartClickHandler = async () => {
        if (!productsExist) {
            return;
        }
        setStatus(addToCartStatus.loading);

        const reversedList = [...productInfos].reverse();
        const cartLinesCollection = (await addCartLines({ productInfos: reversedList, isQuickOrder: true } as any)) as CartLineCollectionModel;

        setStatus(addToCartStatus.success);

        if (showAddToCartConfirmationDialog) {
            const isQtyAdjusted = cartLinesCollection.cartLines?.some(cartLine => cartLine.isQtyAdjusted) ?? false;
            const qtyExceededInventory =
                cartLinesCollection.cartLines?.some(cartLine => (cartLine.qtyOrdered || 0) > cartLine.qtyOnHand) ??
                false;
            toasterContext.addToast({
                body: (
                    <ProductAddedToCartMessage
                        isQtyAdjusted={isQtyAdjusted}
                        multipleProducts={true}
                        {...{ qtyExceededInventory }}
                    />
                ),
                messageType: "success",
            });
        }
        clearProducts();
        reset();
        loadCurrentCart({ shouldLoadFullCart: true });
    };

    const addToListClickHandler = () => {
        if (!productsExist || !canAddSomeToList) {
            return;
        }

        const productInfosToAdd = productInfos.filter(o => products.find(p => p?.id === o.productId)?.canAddToWishlist);
        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos: productInfosToAdd,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: productInfosToAdd });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Button
                {...styles.addAllToCartButton}
                onClick={addAllToCartClickHandler}
                disabled={!allQtysIsValid || !productsExist || status === addToCartStatus.loading}
                className={status.status}
                icon={iconConfig}
                data-test-selector="quickOrder_addAllProductsToCart"
                aria-label="Add to Cart"
                {...{ isLoading: status === addToCartStatus.loading }}
            >
                {translate("Add to Cart")}
            </Button>
            <Link
                {...styles.addToListLink}
                onClick={addToListClickHandler}
                disabled={!allQtysIsValid || !productsExist || !canAddSomeToList}
                aria-label="Add to Product List"
            >
                {translate("or Add to Product List")}
            </Link>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniQuickOrderActions));
